import { Box, Typography } from "@mui/material";
import { useCollections } from "api/collections";
import { MoreElements } from "components/MoreElements/MoreElements";
import { KeyValue } from "components/MoreElements/types";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useMessage } from "hooks/intl";
import {
  CampaignConditionNewFormat,
  NewFormatConditionType,
  OperatorId,
  TransactionItem,
  TransactionItemFilterType,
} from "types/campaigns";
import { operators } from "../../../../campaignHelpers";
import { useStyles } from "../ConditionReadOnly";
import { ParamsReadOnly } from "./ParamsReadOnly";

interface Props {
  conditionObject: TransactionItemFilterType | CampaignConditionNewFormat;
  transactionItemsFilters?: TransactionItem[];
  "data-test-id": string;
  error?: any;
}

const maxVisibleConditionValuesCount = 3;

export const NewFormatConditionReadOnly = ({
  conditionObject,
  transactionItemsFilters,
  "data-test-id": dataTestId,
  error,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles();
  const operator = conditionObject?.condition?.operator;
  const value = conditionObject?.condition?.value;
  const operatorLabel =
    operators[conditionObject?.type]?.find((item) => item.id === operator)
      ?.label ?? "";
  const isCollectionsWithKeyValue =
    value !== null &&
    typeof value === "object" &&
    "groupValuesIds" in value &&
    "labelKey" in value;
  const collectionKey = isCollectionsWithKeyValue
    ? (
        value as {
          labelKey: string;
          groupValuesIds: string[];
        }
      ).labelKey
    : "";
  const operatorError = error?.condition?.operator;
  const isOperatorError = !!operatorError;
  const filterName = transactionItemsFilters?.find(
    (item) =>
      (conditionObject as CampaignConditionNewFormat)?.value?.filterCode ===
      item.code,
  )?.name;

  const { useCollectionsList } = useCollections();
  const { data: collections } = useCollectionsList();

  const setConditionLabel = () => {
    switch (conditionObject?.label) {
      case "quantityOfFilteredTransaction":
        return `${filterName ?? ""} ${message({ id: "transactionQuantity" }).toLowerCase()}`;
      case "valueOfFilteredTransaction":
        return `${filterName ?? ""} ${message({ id: "transactionValue" }).toLowerCase()}`;
      default:
        return message({ id: conditionObject?.label });
    }
  };

  const formatValue = () => {
    switch (conditionObject.type) {
      case NewFormatConditionType.STRING:
        switch (operator) {
          case OperatorId.EQUAL:
          case OperatorId.NOT_EQUAL:
          case OperatorId.STARTS_WITH:
          case OperatorId.ENDS_WITH:
          case OperatorId.MATCH:
          case OperatorId.NOT_MATCH:
            return <Box className={classes.box}>{value as string}</Box>;
          case OperatorId.IN:
          case OperatorId.NOT_IN:
            return (
              <>
                {(value as string[])
                  ?.slice(0, maxVisibleConditionValuesCount)
                  ?.map((item, index) => (
                    <Box key={index} className={classes.box}>
                      {item}
                    </Box>
                  ))}
                <MoreElements
                  totalItemsNumber={(value as string[])?.length}
                  maxVisibleElementsCount={maxVisibleConditionValuesCount}
                  tooltipData={{
                    type: "labels",
                    data: (value as string[])?.slice(
                      maxVisibleConditionValuesCount,
                    ),
                  }}
                />
              </>
            );
          case OperatorId.IN_VALUE_GROUP:
          case OperatorId.NOT_IN_VALUE_GROUP:
            const groupValues = (
              value as { groupValuesIds: string[] }
            ).groupValuesIds?.map(
              (id) =>
                collections?.items?.find((item) => item.groupOfValuesId === id)
                  ?.name || id,
            );
            return (
              <>
                {groupValues
                  ?.slice(0, maxVisibleConditionValuesCount)
                  ?.map((name, index) => (
                    <Box key={index} className={classes.box}>
                      {name}
                    </Box>
                  ))}
                <MoreElements
                  totalItemsNumber={groupValues?.length}
                  maxVisibleElementsCount={maxVisibleConditionValuesCount}
                  tooltipData={{
                    type: "labels",
                    data: groupValues?.slice(maxVisibleConditionValuesCount),
                  }}
                />
              </>
            );
          default:
            return null;
        }
      case NewFormatConditionType.NUMBER:
        switch (operator) {
          case OperatorId.EQUAL:
          case OperatorId.NOT_EQUAL:
          case OperatorId.LESS:
          case OperatorId.LESS_OR_EQUAL:
          case OperatorId.GREATER:
          case OperatorId.GREATER_OR_EQUAL:
            return <Box className={classes.box}>{value as number}</Box>;
          case OperatorId.IN:
          case OperatorId.NOT_IN:
            return (
              <>
                {(value as number[])
                  ?.slice(0, maxVisibleConditionValuesCount)
                  ?.map((item, index) => (
                    <Box key={index} className={classes.box}>
                      {item}
                    </Box>
                  ))}
                <MoreElements
                  totalItemsNumber={(value as string[])?.length}
                  maxVisibleElementsCount={maxVisibleConditionValuesCount}
                  tooltipData={{
                    type: "labels",
                    data: (value as string[])?.slice(
                      maxVisibleConditionValuesCount,
                    ),
                  }}
                />
              </>
            );
          case OperatorId.BETWEEN:
          case OperatorId.NOT_BETWEEN:
            return (
              <>
                <Box className={classes.box}>
                  {
                    (
                      value as {
                        from: number;
                        to: number;
                      }
                    ).from
                  }
                </Box>
                <Typography>{message({ id: "and" }).toLowerCase()}</Typography>
                <Box className={classes.box}>
                  {
                    (
                      value as {
                        from: number;
                        to: number;
                      }
                    ).to
                  }
                </Box>
              </>
            );
          default:
            return null;
        }
      case NewFormatConditionType.LABELS_ARRAY:
        switch (operator) {
          case OperatorId.ONE_OF:
          case OperatorId.NOT_ONE_OF:
            return (
              <>
                {(value as KeyValue[])
                  ?.slice(0, maxVisibleConditionValuesCount)
                  ?.map((item) => (
                    <Box className={classes.box}>
                      <span>{item.key}</span>:<span>{item.value}</span>
                    </Box>
                  ))}

                <MoreElements
                  totalItemsNumber={(value as KeyValue[])?.length}
                  tooltipData={{
                    type: "keyValue",
                    data: (value as KeyValue[])?.slice(
                      maxVisibleConditionValuesCount,
                    ),
                  }}
                />
              </>
            );
          case OperatorId.ONE_OF_VALUE_GROUP:
          case OperatorId.NOT_ONE_OF_VALUE_GROUP:
            const groupsLabels = (
              value as {
                labelKey: string;
                groupValuesIds: string[];
              }
            )?.groupValuesIds?.map(
              (id) =>
                collections?.items?.find((item) => item.groupOfValuesId === id)
                  ?.name || id,
            );

            return (
              <>
                {groupsLabels
                  ?.slice(0, maxVisibleConditionValuesCount)
                  ?.map((label, idx) => {
                    return (
                      <Box key={idx} className={classes.box}>
                        {label}
                      </Box>
                    );
                  })}
                <MoreElements
                  totalItemsNumber={groupsLabels?.length}
                  tooltipData={{
                    type: "labels",
                    data: groupsLabels?.slice(maxVisibleConditionValuesCount),
                  }}
                />
              </>
            );
          default:
            return null;
        }
      default:
        return null;
    }
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.box} data-test-id={`${dataTestId}-attribute`}>
        {setConditionLabel()}
      </Box>
      <ParamsReadOnly conditionObject={conditionObject} />
      {isCollectionsWithKeyValue ? (
        <>
          <Typography>{message({ id: "withAKey" }).toLowerCase()}</Typography>
          <Box className={classes.box}>{collectionKey}</Box>
        </>
      ) : null}
      <Tooltip text={isOperatorError ? (operatorError as string[]).join() : ""}>
        <span
          className={`${classes.operator} ${isOperatorError ? classes.textError : ""}`}
          data-test-id={`${dataTestId}-operator`}
        >
          {message({
            id: operatorLabel || "condition",
          })}
        </span>
      </Tooltip>
      <Box className={classes.boxWrapper} data-test-id={`${dataTestId}-value`}>
        {formatValue()}
      </Box>
    </Box>
  );
};
